import React, { useCallback, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprite, SparkleBurst, CharacterView, PromptCapsule } from "./fx";

// LeafPile — "Leaf Pile Party". Tap scattered leaves to sweep them into a
// growing pile, then tap the full pile to jump in: a joyful leaf burst.
// Faithful port of iOS CityHelperLeafPileView.

const PALETTE = ["#ed7330", "#f2b333", "#cc4d38", "#b88533"];
const PARTY = Array.from({ length: 16 }, (_, i) => {
  const angle = (i / 16) * Math.PI * 2;
  const reach = 90 + ((i * 37) % 70);
  return {
    id: i,
    x: Math.cos(angle) * reach,
    y: -reach * 0.85 + Math.sin(angle) * reach * 0.3,
    rot: (i * 53) % 360,
  };
});

const seededLeaves = (count) => {
  let state = (count * 0x9e3779b1 + 0x1234) >>> 0;
  const rand = () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
  return Array.from({ length: count }, (_, i) => {
    const col = count > 1 ? i / (count - 1) : 0.5;
    const x = Math.min(0.9, Math.max(0.1, 0.12 + col * 0.76 + (rand() - 0.5) * 0.1));
    const y = 0.2 + rand() * 0.34;
    return {
      id: i,
      x,
      y,
      rotation: (rand() - 0.5) * 80,
      scale: 0.85 + rand() * 0.3,
      tint: PALETTE[i % PALETTE.length],
    };
  });
};

const LeafPile = ({ payload, sounds, onDone }) => {
  const total = payload.leafCount || 7;
  const leaves = useMemo(() => seededLeaves(total), [total]);
  const [gathered, setGathered] = useState(() => new Set());
  const [phase, setPhase] = useState("gathering"); // gathering | ready | party
  const [sparkle, setSparkle] = useState(0);
  const [partyBurst, setPartyBurst] = useState(0);
  const [flights, setFlights] = useState([]); // ghost leaves mid-flight to the pile

  const pileUnit = { x: 0.5, y: 0.82 };
  const pileLevel = total === 0 ? 0 : gathered.size / total;

  const gather = useCallback(
    (leaf) => {
      if (phase !== "gathering" || gathered.has(leaf.id)) return;
      const next = new Set(gathered).add(leaf.id);
      setGathered(next);
      sounds.playPop((next.size) / Math.max(total, 1));
      const flightId = `${leaf.id}-${next.size}`;
      setFlights((prev) => [...prev, { id: flightId, x: leaf.x, y: leaf.y }]);
      window.setTimeout(() => {
        setFlights((prev) => prev.filter((f) => f.id !== flightId));
        setSparkle((s) => s + 1);
      }, 520);
      if (next.size >= total) {
        window.setTimeout(() => {
          setPhase("ready");
          sounds.speak("Now jump in the pile!");
        }, 400);
      }
    },
    [gathered, phase, sounds, total]
  );

  const jumpIn = useCallback(() => {
    if (phase !== "ready") return;
    setPhase("party");
    sounds.playPop(0.95);
    sounds.playSparkle();
    setPartyBurst((b) => b + 1);
    setSparkle((s) => s + 1);
    window.setTimeout(() => onDone(), 1600);
  }, [onDone, phase, sounds]);

  const showPile = gathered.size > 0 || phase !== "gathering";

  return (
    <div className="ch-activity-body ch-leafpile">
      <PromptCapsule
        text={phase === "gathering" ? payload.prompt : "The pile is ready — tap it to jump in!"}
        sounds={sounds}
      />
      <div className="ch-stage">
        <div className="ch-leaf-bench" style={{ left: "50%", top: "40%" }}>
          <Sprite name="ch_bench" />
        </div>

        {payload.sideCharacter && (
          <div className="ch-leaf-neighbor" style={{ left: "83%", top: "42%" }}>
            <CharacterView character={payload.sideCharacter} happy={phase === "party"} />
          </div>
        )}

        <motion.button
          type="button"
          className={`ch-leaf-pile ${phase === "ready" ? "is-ready" : ""}`}
          style={{ left: `${pileUnit.x * 100}%`, top: `${pileUnit.y * 100}%` }}
          onClick={jumpIn}
          disabled={phase !== "ready"}
          aria-label={phase === "ready" ? "Jump in the leaf pile" : "Leaf pile"}
          animate={
            phase === "ready"
              ? { scale: [1, 1.06, 1] }
              : { scale: showPile ? 0.5 + 0.5 * pileLevel : 0.001, opacity: showPile ? 1 : 0 }
          }
          transition={
            phase === "ready"
              ? { duration: 0.6, repeat: Infinity }
              : { type: "spring", stiffness: 220, damping: 16 }
          }
        >
          {phase === "ready" && <span className="ch-leaf-pile-glow" />}
          <motion.span
            key={gathered.size}
            style={{ display: "block", width: "100%", height: "100%" }}
            initial={{ scaleY: 0.82, scaleX: 1.14 }}
            animate={{ scaleY: 1, scaleX: 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 12 }}
          >
            <Sprite name="ch_leafpile" />
          </motion.span>
        </motion.button>

        {flights.map((f) => (
          <motion.span
            key={f.id}
            className="ch-leaf-flight"
            aria-hidden="true"
            initial={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, opacity: 1, scale: 1, rotate: 0 }}
            animate={{
              left: `${pileUnit.x * 100}%`,
              top: `${pileUnit.y * 100}%`,
              opacity: 0.45,
              scale: 0.55,
              rotate: 210,
            }}
            transition={{ duration: 0.5, ease: "easeIn" }}
          >
            <Sprite name="ch_leaf" />
          </motion.span>
        ))}

        <AnimatePresence>
          {leaves.map(
            (leaf) =>
              !gathered.has(leaf.id) && (
                <motion.button
                  key={leaf.id}
                  type="button"
                  className="ch-leaf-item"
                  style={{ left: `${leaf.x * 100}%`, top: `${leaf.y * 100}%` }}
                  onClick={() => gather(leaf)}
                  aria-label="Sweep the leaf"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: leaf.scale, opacity: 1, rotate: [leaf.rotation - 6, leaf.rotation + 6] }}
                  exit={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                  transition={{
                    scale: { type: "spring", stiffness: 240, damping: 18 },
                    rotate: { duration: 1.8, repeat: Infinity, repeatType: "reverse" },
                  }}
                >
                  <Sprite name="ch_leaf" style={{ filter: `drop-shadow(0 3px 3px rgba(0,0,0,0.2))` }} />
                </motion.button>
              )
          )}
        </AnimatePresence>

        {phase === "party" && (
          <div className="ch-leaf-party" style={{ left: `${pileUnit.x * 100}%`, top: `${pileUnit.y * 100}%` }}>
            {PARTY.map((p) => (
              <motion.span
                key={`${partyBurst}-${p.id}`}
                className="ch-party-leaf"
                initial={{ x: 0, y: 0, scale: 1, opacity: 1, rotate: 0 }}
                animate={{ x: p.x, y: p.y, scale: 0.7, opacity: 0, rotate: p.rot }}
                transition={{ duration: 1.4, ease: "easeOut" }}
              >
                <Sprite name="ch_leaf" />
              </motion.span>
            ))}
          </div>
        )}

        <SparkleBurst trigger={sparkle} reach={110} style={{ left: `${pileUnit.x * 100}%`, top: `${pileUnit.y * 100}%` }} />
      </div>
    </div>
  );
};

export default LeafPile;
